import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const ShepherdStaff = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 22V9" />
    <path d="M14 9c0-3.5 2-5.5 4.5-5.5 1.5 0 2.5 1 2.5 2.5S19.5 9 17 9h-3" />
  </svg>
);

export default function Login() {
  const navigate = useNavigate();
  const { user, login, register } = useAuth();
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) { navigate('/dashboard', { replace: true }); return null; }

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1617176618320-fc065da37736?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGJsdWUlMjBzbGF0ZSUyMGdyYXklMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc2MzYzMTQxfDA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 text-center px-12">
          <ShepherdStaff className="h-16 w-16 text-white mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white font-['Outfit'] tracking-tight">PastorOS</h1>
          <p className="mt-3 text-lg text-slate-300">Because every name matters.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <ShepherdStaff className="h-10 w-10 text-blue-700 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">PastorOS</h1>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl font-['Outfit']">Welcome</CardTitle>
              <CardDescription>Sign in to manage your church community</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <Button
                data-testid="google-signin-button"
                variant="outline"
                className="w-full mb-4 h-11 border-slate-300 hover:bg-slate-50"
                onClick={handleGoogle}
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Sign in with Google
              </Button>

              <Button
                variant="outline"
                className="w-full mb-6 h-11 border-slate-300 hover:bg-slate-50 opacity-50 cursor-not-allowed"
                disabled
                data-testid="facebook-signin-button"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook (Coming Soon)
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">or continue with email</span></div>
              </div>

              {error && <div data-testid="auth-error" className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="login-email">Email</Label>
                      <Input data-testid="login-email" id="login-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@church.com" required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Password</Label>
                      <Input data-testid="login-password" id="login-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required className="mt-1" />
                    </div>
                    <Button data-testid="login-submit" type="submit" className="w-full h-11 bg-blue-700 hover:bg-blue-800" disabled={loading}>
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="reg-name">Full Name</Label>
                      <Input data-testid="register-name" id="reg-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="reg-email">Email</Label>
                      <Input data-testid="register-email" id="reg-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required className="mt-1" />
                    </div>
                    <div>
                      <Label htmlFor="reg-password">Password</Label>
                      <Input data-testid="register-password" id="reg-password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Create password" required className="mt-1" />
                    </div>
                    <Button data-testid="register-submit" type="submit" className="w-full h-11 bg-blue-700 hover:bg-blue-800" disabled={loading}>
                      {loading ? 'Creating account...' : 'Create Account'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
